import { useRef, useEffect, useState } from "react";
import { gsap } from "../../lib/gsap";
import Container from "../layout/Container";
import Section from "../layout/Section";

export default function Contact() {
  const sectionRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        sectionRef.current,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 90%",
          },
        }
      );

      gsap.fromTo(
        leftRef.current,
        { opacity: 0, x: -60 },
        {
          opacity: 1,
          x: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: leftRef.current,
            start: "top 85%",
          },
        }
      );

      gsap.fromTo(
        rightRef.current,
        { opacity: 0, x: 60 },
        {
          opacity: 1,
          x: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: rightRef.current,
            start: "top 85%",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);

    try {
      const response = await fetch("https://formspree.io/f/xvzbygzn", {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        setIsSubmitted(true);
        e.target.reset();
      }
    } catch (error) {
      console.error("Submission error:", error);
    }

    setIsSubmitting(false);
  };

  return (
    <Section
      id="contact"
      className="relative bg-white text-slate-900 py-32 overflow-hidden"
    >
      {/* Subtle depth gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-white pointer-events-none" />

      <Container>
        <div ref={sectionRef} className="relative space-y-24">

          {/* Header */}
          <div className="max-w-4xl">
            <h2 className="text-4xl md:text-6xl font-semibold leading-tight mb-8">
              Partner With Neoraq
            </h2>

            <p className="text-slate-600 text-lg leading-relaxed">
              We collaborate with automotive manufacturers, mobility
              providers, infrastructure operators, and AI research
              institutions to deploy scalable autonomous systems globally.
            </p>
          </div>

          {/* Grid */}
          <div className="grid md:grid-cols-2 gap-24">

            {/* LEFT SIDE */}
            <div ref={leftRef} className="space-y-14">

              <div>
                <h3 className="text-xl font-semibold mb-4">
                  Enterprise Deployment
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Licensing, fleet-scale deployment, and commercial
                  autonomy integration across automotive platforms.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4">
                  Technical Partnerships
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  Research collaborations, AI co-development,
                  and advanced perception stack innovation.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold mb-4">
                  General Inquiries
                </h3>
                <p className="text-slate-600">
                  hello@neoraq.ai
                </p>
              </div>

            </div>

            {/* RIGHT SIDE FORM */}
            <div ref={rightRef}>
              {isSubmitted ? (
                <div className="p-10 border border-slate-200 rounded-3xl bg-slate-50">
                  <h3 className="text-2xl font-semibold mb-4">
                    Inquiry Received
                  </h3>
                  <p className="text-slate-600">
                    Our team will evaluate your request and respond shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-10">

                  <div className="group">
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Full Name"
                      className="w-full border-b border-slate-300 py-3 bg-transparent focus:outline-none focus:border-[color:var(--color-accent)] transition-all duration-300"
                    />
                  </div>

                  <div className="group">
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="Email Address"
                      className="w-full border-b border-slate-300 py-3 bg-transparent focus:outline-none focus:border-[color:var(--color-accent)] transition-all duration-300"
                    />
                  </div>

                  <div className="group">
                    <textarea
                      name="message"
                      rows="4"
                      required
                      placeholder="Describe your inquiry"
                      className="w-full border-b border-slate-300 py-3 bg-transparent focus:outline-none focus:border-[color:var(--color-accent)] transition-all duration-300 resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-10 py-4 rounded-full font-medium transition-all duration-300 ${
                      isSubmitting
                        ? "bg-slate-400 text-white cursor-not-allowed"
                        : "bg-[color:var(--color-accent)] text-white hover:scale-105 hover:shadow-lg hover:shadow-indigo-500/20"
                    }`}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Inquiry"}
                  </button>

                </form>
              )}
            </div>

          </div>

        </div>
      </Container>
    </Section>
  );
}